import {
  InboxEntry,
  InboxEntryOrderKeyAssignment,
  RecurrenceInboxEntry,
  TaskGroup,
  TaskLabel,
  TaskSubgroup,
} from '@this+that/models';
import { compareTaskSubgroups } from '@this+that/utils';
import { StateCreator, StoreApi } from 'zustand';

import {
  applyBucketTotalsTransition,
  getCurrentCardStatusFilter,
  handleAdjustBucketTotal,
  handleApplyInboxEntryReplyState,
  handleClearUnitNewReply,
  handleDismissEntryNewReplies,
  handleGetCommunicationsInInboxEntry,
  handleModifyInboxEntryUserCommunication,
  handleReadUserCommunicationsInInboxEntries,
  handleRemoveDraftFromInboxEntries,
  handleRemoveInboxEntry,
  handleRemoveInboxEntryFromActiveList,
  handleRemoveUserCommunicationsFromInboxEntries,
  handleRestoreDismissedReplies,
  handleSaveCommunicationsInInboxEntry,
  handleSaveDetailedInboxEntry,
  handleSaveInboxEntries,
  handleSaveInitialDoBoxBuckets,
  handleSetArchiveLoading,
  handleSetBucketLoading,
  handleSetCommunicationsLoading,
  handleSetInboxEntryOrderKeys,
  handleSetLoading,
  handleSetModalInboxEntry,
  handleSettleDismissedReplies,
  handleUnlinkUserCommunicationsFromInboxEntry,
  handleUnreadUserCommunicationsInInboxEntries,
  handleUpdateInboxEntry,
  handleUpdateInboxEntryFields,
  handleUpdateUserCommunicationLabelsInInboxEntries,
  handleUpsertDraftInInboxEntry,
  handleUpsertEmailThreadHeadInInboxEntries,
  rebuildTaskSubgroupMembership,
} from './handlers';
import {
  ActiveTaskGroupState,
  ActiveTaskSubgroup,
  BucketKey,
  HandlerGet,
  HandlerSet,
  InboxEntriesHandlerSlice,
  InboxEntriesStore,
  RecurrenceEntriesSection,
  RecurrenceEntriesState,
  TaskGroupsState,
} from './types';
import { resolveViewBucketKeyForEntry } from '../../../pages/private/DoBox/utils/resolveBucketKeyForEntry';

// =============================================================================
// Initial state constants
// =============================================================================

export const INITIAL_TASK_GROUPS_STATE: TaskGroupsState = {
  taskGroups: [],
  isLoading: false,
  exclusiveStartKey: null,
};

export const INITIAL_RECURRENCE_ENTRIES_DATA: RecurrenceEntriesState = {
  recurrenceInboxEntries: [],
  isLoading: false,
  exclusiveStartKey: null,
};

export const INITIAL_ACTIVE_TASK_GROUP_STATE: Omit<
  ActiveTaskGroupState,
  'recurrenceEntries'
> = {
  taskGroupId: null,
  inboxEntries: [],
  modalInboxEntry: null,
  taskLabels: [],
  taskSubgroups: [],
  taskSubgroupsLoading: false,
  taskSubgroupsFetchedForTaskGroupId: null,
  taskLabelsLoading: false,
  isLoading: false,
  isArchiveLoading: false,
  communicationsLoading: false,
  bucketPagination: {},
  bucketTotals: {},
};

// =============================================================================
// Namespaced set/get adapters
//
// These wrappers allow all existing handlers (which operate on a flat
// InboxEntriesHandlerSlice) to be reused for any sub-section of the store
// without modification.
// =============================================================================

const makeActiveTaskGroupSet =
  (set: StoreApi<InboxEntriesStore>['setState']): HandlerSet =>
  (updater) => {
    if (typeof updater === 'function') {
      set((fullState) => ({
        taskManager: {
          ...fullState.taskManager,
          activeTaskGroup: {
            ...fullState.taskManager.activeTaskGroup,
            ...(
              updater as (
                state: InboxEntriesHandlerSlice,
              ) => Partial<InboxEntriesHandlerSlice>
            )(fullState.taskManager.activeTaskGroup),
          },
        },
      }));
    } else {
      set((fullState) => ({
        taskManager: {
          ...fullState.taskManager,
          activeTaskGroup: {
            ...fullState.taskManager.activeTaskGroup,
            ...updater,
          },
        },
      }));
    }
  };

const makeActiveTaskGroupGet =
  (get: StoreApi<InboxEntriesStore>['getState']): HandlerGet =>
  () =>
    get().taskManager.activeTaskGroup;

const saveTaskGroups = (
  set: StoreApi<InboxEntriesStore>['setState'],
  taskGroups: TaskGroup[],
  exclusiveStartKey: string | null,
  loadMore?: boolean,
) => {
  if (loadMore) {
    set((state) => ({
      taskManager: {
        ...state.taskManager,
        taskGroups: {
          ...state.taskManager.taskGroups,
          taskGroups: [
            ...state.taskManager.taskGroups.taskGroups,
            ...taskGroups,
          ],
          exclusiveStartKey,
        },
      },
    }));
  } else {
    set((state) => ({
      taskManager: {
        ...state.taskManager,
        taskGroups: {
          ...state.taskManager.taskGroups,
          taskGroups,
          exclusiveStartKey,
        },
      },
    }));
  }
};

const createTaskGroup = (
  set: StoreApi<InboxEntriesStore>['setState'],
  taskGroup: TaskGroup,
) => {
  set((state) => ({
    taskManager: {
      ...state.taskManager,
      taskGroups: {
        ...state.taskManager.taskGroups,
        taskGroups: [taskGroup, ...state.taskManager.taskGroups.taskGroups],
      },
    },
  }));
};

const updateTaskGroup = (
  set: StoreApi<InboxEntriesStore>['setState'],
  taskGroup: TaskGroup,
) => {
  set((state) => ({
    taskManager: {
      ...state.taskManager,
      taskGroups: {
        ...state.taskManager.taskGroups,
        taskGroups: state.taskManager.taskGroups.taskGroups.map((tg) =>
          tg.id === taskGroup.id ? taskGroup : tg,
        ),
      },
    },
  }));
};

const removeTaskGroup = (
  set: StoreApi<InboxEntriesStore>['setState'],
  taskGroupId: string,
) => {
  set((state) => ({
    taskManager: {
      ...state.taskManager,
      taskGroups: {
        ...state.taskManager.taskGroups,
        taskGroups: state.taskManager.taskGroups.taskGroups.filter(
          (tg) => tg.id !== taskGroupId,
        ),
      },
    },
  }));
};

const setTaskGroupsLoading = (
  set: StoreApi<InboxEntriesStore>['setState'],
  isLoading: boolean,
) => {
  set((state) => ({
    taskManager: {
      ...state.taskManager,
      taskGroups: { ...state.taskManager.taskGroups, isLoading },
    },
  }));
};

const saveTaskLabels = (
  set: StoreApi<InboxEntriesStore>['setState'],
  taskLabels: TaskLabel[],
) => {
  set((state) => ({
    taskManager: {
      ...state.taskManager,
      activeTaskGroup: { ...state.taskManager.activeTaskGroup, taskLabels },
    },
  }));
};

const createTaskLabel = (
  set: StoreApi<InboxEntriesStore>['setState'],
  taskLabel: TaskLabel,
) => {
  set((state) => ({
    taskManager: {
      ...state.taskManager,
      activeTaskGroup: {
        ...state.taskManager.activeTaskGroup,
        taskLabels: [
          taskLabel,
          ...state.taskManager.activeTaskGroup.taskLabels,
        ],
      },
    },
  }));
};

const updateTaskLabel = (
  set: StoreApi<InboxEntriesStore>['setState'],
  taskLabel: TaskLabel,
) => {
  set((state) => ({
    taskManager: {
      ...state.taskManager,
      activeTaskGroup: {
        ...state.taskManager.activeTaskGroup,
        taskLabels: state.taskManager.activeTaskGroup.taskLabels.map((tl) =>
          tl.id === taskLabel.id ? taskLabel : tl,
        ),
      },
    },
  }));
};

const removeTaskLabel = (
  set: StoreApi<InboxEntriesStore>['setState'],
  taskLabelId: string,
) => {
  set((state) => ({
    taskManager: {
      ...state.taskManager,
      activeTaskGroup: {
        ...state.taskManager.activeTaskGroup,
        taskLabels: state.taskManager.activeTaskGroup.taskLabels.filter(
          (tl) => tl.id !== taskLabelId,
        ),
      },
    },
  }));
};

const saveTaskSubgroups = (
  set: StoreApi<InboxEntriesStore>['setState'],
  taskSubgroups: TaskSubgroup[],
  taskGroupId: string | null,
) => {
  set((state) => {
    // Staleness guard: a fetch started for an earlier-active group must not
    // clobber the current one on out-of-order resolution (rapid A→B→A switch).
    if (taskGroupId !== state.taskManager.activeTaskGroup.taskGroupId) {
      return state;
    }
    const base: ActiveTaskSubgroup[] = taskSubgroups.map((sg) => ({
      ...sg,
      inboxEntryIds: new Set<string>(),
    }));
    return {
      taskManager: {
        ...state.taskManager,
        activeTaskGroup: {
          ...state.taskManager.activeTaskGroup,
          taskSubgroups: rebuildTaskSubgroupMembership(
            base,
            state.taskManager.activeTaskGroup.inboxEntries,
          ).sort(compareTaskSubgroups),
          taskSubgroupsFetchedForTaskGroupId: taskGroupId,
        },
      },
    };
  });
};

const createTaskSubgroup = (
  set: StoreApi<InboxEntriesStore>['setState'],
  sg: TaskSubgroup,
) => {
  set((state) => ({
    taskManager: {
      ...state.taskManager,
      activeTaskGroup: {
        ...state.taskManager.activeTaskGroup,
        taskSubgroups: [
          ...state.taskManager.activeTaskGroup.taskSubgroups,
          { ...sg, inboxEntryIds: new Set<string>() },
        ].sort(compareTaskSubgroups),
      },
    },
  }));
};

const updateTaskSubgroup = (
  set: StoreApi<InboxEntriesStore>['setState'],
  sg: TaskSubgroup,
) => {
  set((state) => ({
    taskManager: {
      ...state.taskManager,
      activeTaskGroup: {
        ...state.taskManager.activeTaskGroup,
        taskSubgroups: state.taskManager.activeTaskGroup.taskSubgroups
          .map((existing) =>
            existing.id === sg.id
              ? { ...existing, ...sg, inboxEntryIds: existing.inboxEntryIds }
              : existing,
          )
          .sort(compareTaskSubgroups),
      },
    },
  }));
};

const replaceTaskSubgroup = (
  set: StoreApi<InboxEntriesStore>['setState'],
  tempId: string,
  sg: TaskSubgroup,
) => {
  set((state) => ({
    taskManager: {
      ...state.taskManager,
      activeTaskGroup: {
        ...state.taskManager.activeTaskGroup,
        taskSubgroups: state.taskManager.activeTaskGroup.taskSubgroups
          .map((s) =>
            s.id === tempId ? { ...sg, inboxEntryIds: s.inboxEntryIds } : s,
          )
          .sort(compareTaskSubgroups),
      },
    },
  }));
};

const removeTaskSubgroup = (
  set: StoreApi<InboxEntriesStore>['setState'],
  taskSubgroupId: string,
) => {
  const selectedCardStatusFilter = getCurrentCardStatusFilter();

  set((state) => {
    const removed = state.taskManager.activeTaskGroup.taskSubgroups.find(
      (sg) => sg.id === taskSubgroupId,
    );
    const memberIds = removed?.inboxEntryIds ?? new Set<string>();

    const updatedSubgroups =
      state.taskManager.activeTaskGroup.taskSubgroups.filter(
        (sg) => sg.id !== taskSubgroupId,
      );

    const updatedInboxEntries =
      state.taskManager.activeTaskGroup.inboxEntries.map((entry) =>
        memberIds.has(entry.id) ? { ...entry, taskSubgroupId: null } : entry,
      );

    const modalEntry = state.taskManager.activeTaskGroup.modalInboxEntry;
    const updatedModalEntry =
      modalEntry && memberIds.has(modalEntry.id)
        ? { ...modalEntry, taskSubgroupId: null }
        : modalEntry;

    // Every relocated member moves from the deleted subgroup's bucket to
    // whatever bucket it naturally resolves to once ungrouped (usually
    // BACKLOG, but an approval/suggestion pinned to this subgroup reverts to
    // APPROVALS/SUGGESTIONS instead) — mirrors handleUpdateInboxEntryFields'
    // before/after transition. The deleted subgroup's own key no longer
    // corresponds to anything and is dropped entirely.
    const bucketTotalsAfterMoves =
      state.taskManager.activeTaskGroup.inboxEntries.reduce(
        (acc, entry) =>
          memberIds.has(entry.id)
            ? applyBucketTotalsTransition(
                acc,
                resolveViewBucketKeyForEntry(entry, selectedCardStatusFilter),
                resolveViewBucketKeyForEntry(
                  { ...entry, taskSubgroupId: null },
                  selectedCardStatusFilter,
                ),
              )
            : acc,
        state.taskManager.activeTaskGroup.bucketTotals,
      );
    const bucketTotals = { ...bucketTotalsAfterMoves };
    delete bucketTotals[taskSubgroupId];

    return {
      taskManager: {
        ...state.taskManager,
        activeTaskGroup: {
          ...state.taskManager.activeTaskGroup,
          taskSubgroups: updatedSubgroups,
          inboxEntries: updatedInboxEntries,
          modalInboxEntry: updatedModalEntry,
          bucketTotals,
        },
      },
    };
  });
};

const setTaskSubgroupsLoading = (
  set: StoreApi<InboxEntriesStore>['setState'],
  isLoading: boolean,
) => {
  set((state) => ({
    taskManager: {
      ...state.taskManager,
      activeTaskGroup: {
        ...state.taskManager.activeTaskGroup,
        taskSubgroupsLoading: isLoading,
      },
    },
  }));
};

const setTaskLabelsLoading = (
  set: StoreApi<InboxEntriesStore>['setState'],
  isLoading: boolean,
) => {
  set((state) => ({
    taskManager: {
      ...state.taskManager,
      activeTaskGroup: {
        ...state.taskManager.activeTaskGroup,
        taskLabelsLoading: isLoading,
      },
    },
  }));
};

// Standalone taskSubgroupId reassignment path used directly by drag-and-drop
// and "Move to subgroup" (see DoBoxReorderPersistenceContext.tsx and
// useMoveInboxEntryToTaskSubgroup.ts) — it does not funnel through
// handleUpdateInboxEntry/Fields, so bucketTotals delta accounting is
// duplicated here rather than centralized in handlers.ts.
const assignInboxEntryToSubgroup = (
  set: StoreApi<InboxEntriesStore>['setState'],
  inboxEntryId: string,
  taskSubgroupId: string | null,
  orderKey?: string,
) => {
  const selectedCardStatusFilter = getCurrentCardStatusFilter();

  set((state) => {
    const orderKeyUpdate = orderKey !== undefined ? { orderKey } : {};
    const existingEntry = state.taskManager.activeTaskGroup.inboxEntries.find(
      (entry) => entry.id === inboxEntryId,
    );
    const updatedInboxEntries =
      state.taskManager.activeTaskGroup.inboxEntries.map((entry) =>
        entry.id === inboxEntryId
          ? { ...entry, taskSubgroupId, ...orderKeyUpdate }
          : entry,
      );

    const modalEntry = state.taskManager.activeTaskGroup.modalInboxEntry;
    const updatedModalEntry =
      modalEntry?.id === inboxEntryId
        ? { ...modalEntry, taskSubgroupId, ...orderKeyUpdate }
        : modalEntry;

    const updatedSubgroups =
      state.taskManager.activeTaskGroup.taskSubgroups.map((sg) => {
        const has = sg.inboxEntryIds.has(inboxEntryId);
        const shouldHave = sg.id === taskSubgroupId;
        if (has === shouldHave) {
          return sg;
        }
        const next = new Set(sg.inboxEntryIds);
        if (shouldHave) {
          next.add(inboxEntryId);
        } else {
          next.delete(inboxEntryId);
        }
        return { ...sg, inboxEntryIds: next };
      });

    const bucketTotals = existingEntry
      ? applyBucketTotalsTransition(
          state.taskManager.activeTaskGroup.bucketTotals,
          resolveViewBucketKeyForEntry(existingEntry, selectedCardStatusFilter),
          resolveViewBucketKeyForEntry(
            { ...existingEntry, taskSubgroupId },
            selectedCardStatusFilter,
          ),
        )
      : state.taskManager.activeTaskGroup.bucketTotals;

    return {
      taskManager: {
        ...state.taskManager,
        activeTaskGroup: {
          ...state.taskManager.activeTaskGroup,
          inboxEntries: updatedInboxEntries,
          modalInboxEntry: updatedModalEntry,
          taskSubgroups: updatedSubgroups,
          bucketTotals,
        },
      },
    };
  });
};

const setActiveSubgroupState = (
  set: StoreApi<InboxEntriesStore>['setState'],
  taskSubgroups: ActiveTaskSubgroup[],
  inboxEntries: InboxEntry[],
  bucketTotals?: Record<BucketKey, number>,
) => {
  set((state) => ({
    taskManager: {
      ...state.taskManager,
      activeTaskGroup: {
        ...state.taskManager.activeTaskGroup,
        taskSubgroups,
        inboxEntries,
        ...(bucketTotals ? { bucketTotals } : {}),
      },
    },
  }));
};

const removeEntryIdFromSubgroups = (
  set: StoreApi<InboxEntriesStore>['setState'],
  inboxEntryId: string,
) => {
  set((state) => ({
    taskManager: {
      ...state.taskManager,
      activeTaskGroup: {
        ...state.taskManager.activeTaskGroup,
        taskSubgroups: state.taskManager.activeTaskGroup.taskSubgroups.map(
          (sg) => {
            if (!sg.inboxEntryIds.has(inboxEntryId)) {
              return sg;
            }
            const next = new Set(sg.inboxEntryIds);
            next.delete(inboxEntryId);
            return { ...sg, inboxEntryIds: next };
          },
        ),
      },
    },
  }));
};

const setActiveTaskGroup = (
  set: StoreApi<InboxEntriesStore>['setState'],
  taskGroupId: string | null,
) => {
  set((state) => {
    // Switching to the already-active group must NOT wipe its loaded entries —
    // a remount (e.g. header swap on resize) re-runs the mount effect with the
    // same id; without this guard that clears inboxEntries. Use resetActiveTaskGroup
    // for an explicit reset.
    if (state.taskManager.activeTaskGroup.taskGroupId === taskGroupId) {
      return state;
    }
    const currentLoading = state.taskManager.activeTaskGroup.isLoading;

    return {
      taskManager: {
        ...state.taskManager,
        activeTaskGroup: {
          ...state.taskManager.activeTaskGroup,
          ...INITIAL_ACTIVE_TASK_GROUP_STATE,
          taskGroupId,
          isLoading: currentLoading,
          recurrenceEntries: {
            ...state.taskManager.activeTaskGroup.recurrenceEntries,
            ...INITIAL_RECURRENCE_ENTRIES_DATA,
          },
        },
      },
    };
  });
};

const resetActiveTaskGroup = (set: StoreApi<InboxEntriesStore>['setState']) => {
  set((state) => {
    return {
      taskManager: {
        ...state.taskManager,
        activeTaskGroup: {
          ...state.taskManager.activeTaskGroup,
          ...INITIAL_ACTIVE_TASK_GROUP_STATE,
          recurrenceEntries: {
            ...state.taskManager.activeTaskGroup.recurrenceEntries,
            ...INITIAL_RECURRENCE_ENTRIES_DATA,
          },
        },
      },
    };
  });
};

const saveRecurrenceInboxEntries = (
  set: StoreApi<InboxEntriesStore>['setState'],
  entries: RecurrenceInboxEntry[],
  exclusiveStartKey: string | null,
  loadMore?: boolean,
) => {
  set((state) => ({
    taskManager: {
      ...state.taskManager,
      activeTaskGroup: {
        ...state.taskManager.activeTaskGroup,
        recurrenceEntries: {
          ...state.taskManager.activeTaskGroup.recurrenceEntries,
          exclusiveStartKey,
          recurrenceInboxEntries: loadMore
            ? [
                ...state.taskManager.activeTaskGroup.recurrenceEntries
                  .recurrenceInboxEntries,
                ...entries,
              ]
            : entries,
        },
      },
    },
  }));
};

const addRecurrenceInboxEntry = (
  set: StoreApi<InboxEntriesStore>['setState'],
  entry: RecurrenceInboxEntry,
) => {
  set((state) => ({
    taskManager: {
      ...state.taskManager,
      activeTaskGroup: {
        ...state.taskManager.activeTaskGroup,
        recurrenceEntries: {
          ...state.taskManager.activeTaskGroup.recurrenceEntries,
          recurrenceInboxEntries: [
            entry,
            ...state.taskManager.activeTaskGroup.recurrenceEntries
              .recurrenceInboxEntries,
          ],
        },
      },
    },
  }));
};

const updateRecurrenceInboxEntry = (
  set: StoreApi<InboxEntriesStore>['setState'],
  entry: RecurrenceInboxEntry,
) => {
  set((state) => ({
    taskManager: {
      ...state.taskManager,
      activeTaskGroup: {
        ...state.taskManager.activeTaskGroup,
        recurrenceEntries: {
          ...state.taskManager.activeTaskGroup.recurrenceEntries,
          recurrenceInboxEntries:
            state.taskManager.activeTaskGroup.recurrenceEntries.recurrenceInboxEntries.map(
              (e) => (e.id === entry.id ? entry : e),
            ),
        },
      },
    },
  }));
};

const removeRecurrenceInboxEntry = (
  set: StoreApi<InboxEntriesStore>['setState'],
  id: string,
) => {
  set((state) => ({
    taskManager: {
      ...state.taskManager,
      activeTaskGroup: {
        ...state.taskManager.activeTaskGroup,
        recurrenceEntries: {
          ...state.taskManager.activeTaskGroup.recurrenceEntries,
          recurrenceInboxEntries:
            state.taskManager.activeTaskGroup.recurrenceEntries.recurrenceInboxEntries.filter(
              (e) => e.id !== id,
            ),
        },
      },
    },
  }));
};

const setRecurrenceEntriesLoading = (
  set: StoreApi<InboxEntriesStore>['setState'],
  isLoading: boolean,
) => {
  set((state) => ({
    taskManager: {
      ...state.taskManager,
      activeTaskGroup: {
        ...state.taskManager.activeTaskGroup,
        recurrenceEntries: {
          ...state.taskManager.activeTaskGroup.recurrenceEntries,
          isLoading,
        },
      },
    },
  }));
};

export const createInboxEntriesSlice: StateCreator<InboxEntriesStore> = (
  set,
  get,
) => {
  const atgSet = makeActiveTaskGroupSet(set);
  const atgGet = makeActiveTaskGroupGet(get);

  return {
    taskManager: {
      taskGroups: {
        ...INITIAL_TASK_GROUPS_STATE,

        saveTaskGroups: (taskGroupsData, exclusiveStartKey, loadMore) =>
          saveTaskGroups(set, taskGroupsData, exclusiveStartKey, loadMore),
        createTaskGroup: (taskGroup) => createTaskGroup(set, taskGroup),
        updateTaskGroup: (taskGroup) => updateTaskGroup(set, taskGroup),
        removeTaskGroup: (taskGroupId) => removeTaskGroup(set, taskGroupId),
        setTaskGroupsLoading: (isLoading) =>
          setTaskGroupsLoading(set, isLoading),
      },

      activeTaskGroup: {
        ...INITIAL_ACTIVE_TASK_GROUP_STATE,
        setLoading: (isLoading) => handleSetLoading(atgSet, isLoading),
        setBucketLoading: (bucketKey, isLoading) =>
          handleSetBucketLoading(atgSet, bucketKey, isLoading),
        adjustBucketTotal: (bucketKey, delta) =>
          handleAdjustBucketTotal(atgSet, bucketKey, delta),
        setCommunicationsLoading: (communicationsLoading) =>
          handleSetCommunicationsLoading(atgSet, communicationsLoading),
        saveInboxEntries: (
          inboxEntries,
          exclusiveStartKey,
          loadMore,
          bucketKey,
        ) => {
          handleSaveInboxEntries(
            atgSet,
            inboxEntries,
            exclusiveStartKey,
            loadMore,
            bucketKey,
          );
          set((state) => ({
            taskManager: {
              ...state.taskManager,
              activeTaskGroup: {
                ...state.taskManager.activeTaskGroup,
                taskSubgroups: rebuildTaskSubgroupMembership(
                  state.taskManager.activeTaskGroup.taskSubgroups,
                  state.taskManager.activeTaskGroup.inboxEntries,
                ),
              },
            },
          }));
        },
        saveInitialDoBoxBuckets: (results, merge) => {
          handleSaveInitialDoBoxBuckets(atgSet, results, merge);
          set((state) => ({
            taskManager: {
              ...state.taskManager,
              activeTaskGroup: {
                ...state.taskManager.activeTaskGroup,
                taskSubgroups: rebuildTaskSubgroupMembership(
                  state.taskManager.activeTaskGroup.taskSubgroups,
                  state.taskManager.activeTaskGroup.inboxEntries,
                ),
              },
            },
          }));
        },
        saveCommunicationsInInboxEntry: (inboxEntryId, communications) =>
          handleSaveCommunicationsInInboxEntry(
            atgSet,
            inboxEntryId,
            communications,
          ),
        updateInboxEntry: (updatedEntry) => {
          handleUpdateInboxEntry(atgSet, atgGet, updatedEntry);
          if (updatedEntry.taskSubgroupId !== undefined) {
            assignInboxEntryToSubgroup(
              set,
              updatedEntry.id,
              updatedEntry.taskSubgroupId ?? null,
            );
          }
        },
        updateInboxEntryFields: (inboxEntryId, fields) => {
          handleUpdateInboxEntryFields(atgSet, inboxEntryId, fields);
          if (fields.taskSubgroupId !== undefined) {
            assignInboxEntryToSubgroup(
              set,
              inboxEntryId,
              fields.taskSubgroupId ?? null,
            );
          }
        },
        saveDetailedInboxEntry: (detailedEntry) => {
          handleSaveDetailedInboxEntry(atgSet, atgGet, detailedEntry);
          if (detailedEntry.taskSubgroupId !== undefined) {
            assignInboxEntryToSubgroup(
              set,
              detailedEntry.id,
              detailedEntry.taskSubgroupId ?? null,
            );
          }
        },
        setModalInboxEntry: (entry) => handleSetModalInboxEntry(atgSet, entry),
        removeInboxEntry: async (inboxEntryId) => {
          const result = await handleRemoveInboxEntry(
            atgSet,
            atgGet,
            inboxEntryId,
          );
          removeEntryIdFromSubgroups(set, inboxEntryId);
          return result;
        },
        removeInboxEntryFromActiveList: (updatedEntry) => {
          handleRemoveInboxEntryFromActiveList(atgSet, updatedEntry);
          removeEntryIdFromSubgroups(set, updatedEntry.id);
        },
        removeUserCommunicationsFromInboxEntries: (messageIds) =>
          handleRemoveUserCommunicationsFromInboxEntries(atgSet, messageIds),
        unlinkUserCommunicationsFromInboxEntry: (inboxEntryId, messageIds) =>
          handleUnlinkUserCommunicationsFromInboxEntry(
            atgSet,
            inboxEntryId,
            messageIds,
          ),
        readUserCommunicationsInInboxEntries: (messageIds) =>
          handleReadUserCommunicationsInInboxEntries(atgSet, messageIds),
        unreadUserCommunicationsInInboxEntries: (messageIds) =>
          handleUnreadUserCommunicationsInInboxEntries(atgSet, messageIds),
        applyInboxEntryReplyState: (payload) =>
          handleApplyInboxEntryReplyState(atgSet, payload),
        clearUnitNewReply: (inboxEntryId, unitKey) =>
          handleClearUnitNewReply(atgSet, inboxEntryId, unitKey),
        dismissEntryNewReplies: (inboxEntryId, messageIds) =>
          handleDismissEntryNewReplies(
            atgSet,
            atgGet,
            inboxEntryId,
            messageIds,
          ),
        restoreDismissedReplies: (snapshot) =>
          handleRestoreDismissedReplies(atgSet, snapshot),
        settleDismissedReplies: (snapshot, newReplies) =>
          handleSettleDismissedReplies(atgSet, snapshot, newReplies),
        upsertEmailThreadHeadInInboxEntries: (head) =>
          handleUpsertEmailThreadHeadInInboxEntries(atgSet, head),
        getCommunicationsByInboxEntryId: (inboxEntryId) =>
          handleGetCommunicationsInInboxEntry(atgGet, inboxEntryId),
        setArchiveLoading: (isLoading) =>
          handleSetArchiveLoading(atgSet, isLoading),
        upsertDraftInInboxEntry: (
          inboxEntryId,
          draftCommunication,
          messageId,
        ) =>
          handleUpsertDraftInInboxEntry(
            atgSet,
            inboxEntryId,
            draftCommunication,
            messageId,
          ),
        removeDraftFromInboxEntries: (draftId) =>
          handleRemoveDraftFromInboxEntries(atgSet, draftId),
        modifyInboxEntryUserCommunication: (inboxEntryId, userCommunication) =>
          handleModifyInboxEntryUserCommunication(
            atgSet,
            inboxEntryId,
            userCommunication,
          ),
        updateUserCommunicationLabelsInInboxEntries: (
          messageIds,
          updateLabels,
        ) =>
          handleUpdateUserCommunicationLabelsInInboxEntries(
            atgSet,
            messageIds,
            updateLabels,
          ),
        setActiveTaskGroup: (taskGroupId) =>
          setActiveTaskGroup(set, taskGroupId),
        saveTaskLabels: (taskLabels) => saveTaskLabels(set, taskLabels),
        createTaskLabel: (taskLabel) => createTaskLabel(set, taskLabel),
        updateTaskLabel: (taskLabel) => updateTaskLabel(set, taskLabel),
        removeTaskLabel: (taskLabelId) => removeTaskLabel(set, taskLabelId),
        resetActiveTaskGroup: () => resetActiveTaskGroup(set),
        saveTaskSubgroups: (taskSubgroups, taskGroupId) =>
          saveTaskSubgroups(set, taskSubgroups, taskGroupId),
        createTaskSubgroup: (taskSubgroup) =>
          createTaskSubgroup(set, taskSubgroup),
        updateTaskSubgroup: (taskSubgroup) =>
          updateTaskSubgroup(set, taskSubgroup),
        replaceTaskSubgroup: (tempId, taskSubgroup) =>
          replaceTaskSubgroup(set, tempId, taskSubgroup),
        removeTaskSubgroup: (taskSubgroupId) =>
          removeTaskSubgroup(set, taskSubgroupId),
        setTaskSubgroupsLoading: (isLoading) =>
          setTaskSubgroupsLoading(set, isLoading),
        setTaskLabelsLoading: (isLoading) =>
          setTaskLabelsLoading(set, isLoading),
        assignInboxEntryToSubgroup: (inboxEntryId, taskSubgroupId, orderKey) =>
          assignInboxEntryToSubgroup(
            set,
            inboxEntryId,
            taskSubgroupId,
            orderKey,
          ),
        setActiveSubgroupState: (taskSubgroups, inboxEntries, bucketTotals) =>
          setActiveSubgroupState(
            set,
            taskSubgroups,
            inboxEntries,
            bucketTotals,
          ),
        setInboxEntryOrderKeys: (assignments: InboxEntryOrderKeyAssignment[]) =>
          handleSetInboxEntryOrderKeys(atgSet, assignments),
        recurrenceEntries: {
          ...INITIAL_RECURRENCE_ENTRIES_DATA,
          saveRecurrenceInboxEntries: (entries, exclusiveStartKey, loadMore) =>
            saveRecurrenceInboxEntries(
              set,
              entries,
              exclusiveStartKey,
              loadMore,
            ),
          addRecurrenceInboxEntry: (entry) =>
            addRecurrenceInboxEntry(set, entry),
          updateRecurrenceInboxEntry: (entry) =>
            updateRecurrenceInboxEntry(set, entry),
          removeRecurrenceInboxEntry: (id) =>
            removeRecurrenceInboxEntry(set, id),
          setRecurrenceEntriesLoading: (isLoading) =>
            setRecurrenceEntriesLoading(set, isLoading),
        } as RecurrenceEntriesSection,
      },
    },
    resetState: () =>
      set((state) => {
        return {
          taskManager: {
            taskGroups: {
              ...state.taskManager.taskGroups,
              ...INITIAL_TASK_GROUPS_STATE,
            },
            activeTaskGroup: {
              ...state.taskManager.activeTaskGroup,
              ...INITIAL_ACTIVE_TASK_GROUP_STATE,
              taskGroupId: state.taskManager.activeTaskGroup.taskGroupId,
              recurrenceEntries: {
                ...state.taskManager.activeTaskGroup.recurrenceEntries,
                ...INITIAL_RECURRENCE_ENTRIES_DATA,
              },
            },
          },
        };
      }),
  };
};
